import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import AddFieldBtn from "../../common/buttons/AddFieldBtn";
import CardComponentContainer from "../../common/cardContainers/CardComponentContainer";
import FormField from "../../common/forms/FormField";
import SimpleGridContainer from "../../common/forms/SimpleGridContainer";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import BtnLargeSolid from "../../common/buttons/BtnLargeSolid";
import alertCircle from "../../../assets/icons/alert-circle-2.svg";
import DeleteButton from "../../../assets/icons/delete.png";
import AppContext from "../../../AppContext";
import SaveButtonWindowHeader from "../../common/mainApp/mainWindowHeaders/SaveButtonWindowHeader";
import * as Library from "../../../utils/Library";
import { Tooltip } from "antd";

// #region SCOPED CSS
const StyledPage = styled.div``;

const TooltipStyle = styled.div`
    &.frame-1000011442,
    &.frame-1000011442 * {
        box-sizing: border-box;
    }
    &.frame-1000011442 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        flex-shrink: 0;
        //width: 168px;
        position: relative;
    }
    .bo-stage-b-buttons-inverted {
        background: var(--buttons-all-platforms-gray-500, #10141b);
        border-radius: 4px;
        padding: 8px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: center;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        position: relative;
    }
    .total-in-use-2-products-get-orders-get {
        color: var(--buttons-all-platforms-gray-100, #d1d1d1);
        font-size: 14px;
        letter-spacing: 0.0125em;
        font-weight: 400;
        position: relative;
        align-self: stretch;
        text-align: left;
    }
    .polygon-1 {
        margin: -6px 0 0 0;
        border-radius: 0px;
        flex-shrink: 0;
        width: 23px;
        height: 13px;
        position: relative;
        overflow: visible;
    }
`;

// #endregion

export default function POSConnectFields() {
    document.title = `${APP_TITLE} - Connect Fields`;

    // #region STATES
    const navigations = useSelector(state => state.navigationAccesses);
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setHasUnsavedChanges } = useContext(AppContext);
    const navigate = useNavigate();

    //const formConnectFields = useRef();
    const formConnectFieldsRefs = [];

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData,
        navigations: navigations
    };
    const [isLoading, setIsLoading] = useState(false);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);

    //---------- Header Filters
    const [allPOSList, setAllPOSList] = useState([]);
    const [allEnvironmentsList, setAllEnvironmentsList] = useState([]);
    //----------
    const [selectedPOS, setSelectedPOS] = useState(undefined);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);

    const [connectFieldsValues, setConnectFieldsValues] = useState([]);
    const [formConnectFieldsForms, setFormConnectFieldsForms] = useState([]);

    const [typeOptions] = useState([
        { name: "BOtag Key", value: "BOtag" },
        { name: "Label Brand Key", value: "LabelBrand" },
        { name: "Retail Brand Key", value: "RetailBrand" },
        { name: "Store Key", value: "Store" }
    ]);
    // #endregion

    // #region METHODS
    const onPageStart = async () => {
        // get all POSs
        const posListRaw = JSON.parse(localStorage.getItem("POSs")) || [];
        const allPOS = posListRaw?.map(pos => ({
            value: pos?._id,
            name: pos?.name
        }));
        setAllPOSList(allPOS);
        setSelectedPOS(allPOS.length > 0 ? [{ value: allPOS[0].value, name: allPOS[0].name }] : []);

        // get all environments
        let allEnvironments = posListRaw?.[0]?.environments;
        allEnvironments = allEnvironments?.map(env => ({
            value: env,
            name: env
        }));
        setAllEnvironmentsList(allEnvironments);
        setSelectedEnvironment(
            allEnvironments.length > 0 ? [{ value: allEnvironments[0].value, name: allEnvironments[0].name }] : []
        );

        getConnectFieldsData(allPOS?.[0]?.value, allEnvironments?.[0]?.value);
    };

    const getConnectFieldsData = async (selectedPOSID, selectedEnvironmentID) => {
        const connectFieldsData = [];

        const formData = {
            posID: selectedPOSID,
            environmentName: selectedEnvironmentID
        };
        const res = await Library.makePostRequest("getConnectFieldsData", formData, false, setIsLoading);
        if (res?.data?.status !== 200) return;

        const data = res?.data?.data || [];

        if (data.length === 0) {
            setConnectFieldsValues([]);
            setFormConnectFieldsForms([]);
            return;
        }

        // Loop through the data to extract connect fields
        data.forEach(connectField => {
            const connectFieldData = {
                connectFieldName: connectField.name || "",
                type: connectField.type || "",
                BOTagKeyValue: connectField.predefinedValue || ""
            };
            connectFieldsData.push(connectFieldData);
        });

        const newConnectFieldForms = connectFieldsData.map((_, arrayPos) => getNewConnectFieldForms(arrayPos));

        setFormConnectFieldsForms(newConnectFieldForms);
        setHasUnsavedChanges(false);
        setSaveButtonEnabled(true);

        setConnectFieldsValues(connectFieldsData);
    };

    const onPOSChange = pos => {
        setSelectedPOS([pos]);
    };

    const onEnvironmentChange = environment => {
        setSelectedEnvironment([environment]);
    };

    const checkFormIsValid = () => {
        // Check if all connectFieldsValues are valid by checking if they have required fields filled
        const allFieldsAreValid = connectFieldsValues.every(field => {
            return field.connectFieldName && field.type && field.BOTagKeyValue;
        });

        setSaveButtonEnabled(allFieldsAreValid);
        return allFieldsAreValid;
    };

    const handleSave = async () => {
        const formData = {
            posID: selectedPOS?.[0]?.value || "",
            environmentName: selectedEnvironment?.[0]?.value || "",
            connectFieldsData: connectFieldsValues.map(field => ({
                name: field.connectFieldName,
                type: field.type,
                predefinedValue: field.BOTagKeyValue
            }))
        };

        const res = await Library.makePostRequest("updateConnectFields", formData, false, setIsLoading);
        if (res?.data?.status !== 200) return;

        navigate("/pos/connectfields");
        Library.showToastMessage({
            type: "success",
            message: "Connect Fields updated successfully."
        });
    };

    const handleDelete = arrayPos => {
        if (!connectFieldsValues || connectFieldsValues.length === 0) return;
        if (arrayPos < 0 || arrayPos >= connectFieldsValues.length) return;

        setHasUnsavedChanges(true);
        setConnectFieldsValues(prevValues => {
            const updatedFieldsValues = [...prevValues];
            updatedFieldsValues.splice(arrayPos, 1);
            return updatedFieldsValues;
        });
        setFormConnectFieldsForms(prevForms => {
            const updatedForms = [...prevForms];
            updatedForms.splice(arrayPos, 1);
            return updatedForms;
        });
    };

    const getNewConnectFieldForms = arrayPos => {
        formConnectFieldsRefs[arrayPos] = [];
        const formConnectFieldsRefsTMP = formConnectFieldsRefs[arrayPos];

        const useArray = [];
        //const useArray = ["Products Get", "Orders Get"]; // Example use array, replace with actual data if needed
        const tooltip = <TooltipDescription useArray={useArray} />;
        const tooltipMaxWidth = "200px";

        // translate the type value to the type name
        const typeValue = connectFieldsValues[arrayPos]?.type || "";
        const typeName = typeOptions.find(option => option.value === typeValue)?.name || "";

        const formCompanyFields = [
            <FormField
                type="text"
                title="Connect Field Name"
                tooltip="Connect Field Name"
                requiredType="mandatory"
                varName="connectFieldName"
                ref={el => (formConnectFieldsRefsTMP.connectFieldName = el)}
                onTextChange={item => {
                    setHasUnsavedChanges(true);

                    setConnectFieldsValues(prevValues => {
                        const updatedFieldsValues = [...prevValues];
                        updatedFieldsValues[arrayPos] = {
                            ...updatedFieldsValues[arrayPos],
                            connectFieldName: item
                        };
                        return updatedFieldsValues;
                    });
                }}
                value={connectFieldsValues[arrayPos]?.connectFieldName || ""}
            />,
            <div className="w-100 flex flex-row">
                <FormField
                    type="dropdown"
                    dropdownData={typeOptions}
                    title="Type"
                    //isDropdownSearchable={true}
                    tooltip="Select type"
                    requiredType="mandatory"
                    varName="type"
                    //value={companyIDtype}
                    ref={el => (formConnectFieldsRefsTMP.type = el)}
                    style={{ width: "100%" }}
                    onDropdownChange={item => {
                        setHasUnsavedChanges(true);

                        setConnectFieldsValues(prevValues => {
                            const updatedFieldsValues = [...prevValues];
                            updatedFieldsValues[arrayPos] = {
                                ...updatedFieldsValues[arrayPos],
                                type: item?.value || ""
                            };
                            return updatedFieldsValues;
                        });
                    }}
                    value={typeName}
                />
                <Tooltip
                    title={tooltip}
                    color="#10141b"
                    overlayInnerStyle={{ textAlign: "center" }}
                    overlayStyle={{
                        fontSize: "0.75rem",
                        maxWidth: tooltipMaxWidth,
                        textAlign: "center"
                    }}>
                    <div style={{ marginLeft: "4px", marginTop: "21px" }}>
                        <BtnLargeSolid
                            text={<img src={alertCircle} alt="V" style={{ width: "18px", height: "18px" }} />}
                            style={{ width: "40px", height: "37px" }}
                            onClick={() => {}}
                            normalColor="transparent"
                            activeColor="transparent"
                            hoverColor="transparent"
                            textColor="#ffffff"
                        />
                    </div>
                </Tooltip>
            </div>,
            <FormField
                type="text"
                title="BOtag Key Value"
                tooltip="BOtag Key Value"
                requiredType="mandatory"
                varName="BOTagKeyValue"
                ref={el => (formConnectFieldsRefsTMP.BOTagKeyValue = el)}
                onTextChange={item => {
                    setHasUnsavedChanges(true);

                    setConnectFieldsValues(prevValues => {
                        const updatedFieldsValues = [...prevValues];
                        updatedFieldsValues[arrayPos] = {
                            ...updatedFieldsValues[arrayPos],
                            BOTagKeyValue: item
                        };
                        return updatedFieldsValues;
                    });
                }}
                value={connectFieldsValues[arrayPos]?.BOTagKeyValue || ""}
            />,
            <div className="w-100 flex flex-row justify-content-end">
                <BtnLargeSolid
                    text={<img src={DeleteButton} alt="V" style={{ width: "16px", height: "16px" }} />}
                    style={{
                        width: "40px",
                        height: "37px",
                        marginLeft: "4px",
                        marginTop: "21px"
                    }}
                    onClick={() => {
                        handleDelete(arrayPos);
                    }}
                    normalColor="#10141B"
                    activeColor="#10141B"
                    hoverColor="#10141B"
                    textColor="#ffffff"
                />
            </div>
        ];
        return formCompanyFields;
    };

    const addNewConnectFields = () => {
        const arrayNewPos = formConnectFieldsForms.length;

        const newConnectFieldForms = getNewConnectFieldForms(arrayNewPos);
        setFormConnectFieldsForms(prevForms => [...prevForms, newConnectFieldForms]);

        /* todo: add new object to connectFieldsValues */
        //console.log("formConnectFieldsValues", connectFieldsValues);
        setConnectFieldsValues(prevValues => [
            ...prevValues,
            {
                connectFieldName: "",
                type: "",
                BOTagKeyValue: ""
            }
        ]);

        setHasUnsavedChanges(true);
    };
    // #endregion METHODS

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            onSaveClick: () => {
                handleSave();
                setHasUnsavedChanges(false);
            },
            saveButtonEnabled,
            navigations,
            allPOSList,
            allEnvironmentsList,
            onPOSChange,
            onEnvironmentChange,
            selectedPOS,
            selectedEnvironment
        });
    }, [
        props?.layoutType,
        navigations,
        allPOSList,
        allEnvironmentsList,
        selectedPOS,
        selectedEnvironment,
        connectFieldsValues,
        saveButtonEnabled,
        setHasUnsavedChanges
    ]);

    useEffect(() => {
        onPageStart();
    }, []);

    useEffect(() => {
        // Check if all connect fields are valid whenever connectFieldsValues changes
        checkFormIsValid();

        //refresh the forms when connectFieldsValues changes
        const newConnectFieldForms = connectFieldsValues.map((_, arrayPos) => getNewConnectFieldForms(arrayPos));
        setFormConnectFieldsForms(newConnectFieldForms);
    }, [connectFieldsValues]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <div>
                <FullScreenOverlay show={isLoading} />

                <CardComponentContainer title="Connect Fields">
                    <div style={{ display: "flex", gap: "16px", width: "100%", marginTop: "24px" }}>
                        <SimpleGridContainer numOfFieldsPerLine={4} className="w-100">
                            {formConnectFieldsForms.map((fields, index) => {
                                return fields;
                            })}
                        </SimpleGridContainer>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "0px" }}>
                        <AddFieldBtn onClick={addNewConnectFields} className="mt-0" />
                    </div>
                </CardComponentContainer>
            </div>
        </StyledPage>
    );
    // #endregion
}

// #region FUNCTIONS
function setupHeaders({
    props,
    onSaveClick,
    saveButtonEnabled,
    navigations,
    allPOSList,
    allEnvironmentsList,
    onPOSChange,
    onEnvironmentChange,
    selectedPOS,
    selectedEnvironment
}) {
    const createMode = navigations?.navigationAccesses?.retailers?.createModeOnly;

    //const currentPOSID = localStorage.getItem("currentPOSID");
    //const routePOSEdit = `/pos/edit/${currentPOSID}`;

    props.setControlBarLeftBtns([
        //{ to: "#", disabled: true, title: "Retailers", icon: "fa-magnifying-glass", isSearchToogle: false }
    ]);
    props.setControlBarCenterBtns([
        { to: "/pos", active: false, disabled: false, title: "POS" },
        { to: "/pos/setup", active: false, disabled: false, title: "SETUP" },
        { to: "/pos/connectfields", active: true, disabled: false, title: "CONNECT FIELDS" },
        { to: "/pos/auth", active: false, disabled: false, title: "AUTH" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        { to: "/pos/create", active: false, disabled: true, title: "Add", icon: "plus" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
        //{ to: routePOSEdit, active: false, disabled: !currentPOSID, title: "Edit", icon: "edit" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SaveButtonWindowHeader
                key={window.location.pathname}
                onClick={onSaveClick}
                btnEnabled={saveButtonEnabled}
                btnTitle="UPDATE"
                firstOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onPOSChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedPOS?.[0]?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allEnvironmentsList,
                    onDropdownChange: onEnvironmentChange,
                    type: "singleSelect",
                    selectedValue: selectedEnvironment?.[0]?.name
                }}
            />
        )
    });
}

const TooltipDescription = ({ useArray }) => {
    const notInUse = useArray?.length === 0;

    return (
        <TooltipStyle className="frame-1000011442" style={{ minWidth: notInUse ? "auto" : "168px" }}>
            <div className="bo-stage-b-buttons-inverted">
                {notInUse ? (
                    <div className="total-in-use-2-products-get-orders-get">Not in use</div>
                ) : (
                    <div className="total-in-use-2-products-get-orders-get">
                        Total in use ({useArray?.length || 0}): <br />
                        <br />
                        {useArray?.map((item, index) => (
                            <span key={index}>
                                - {item}
                                {index < useArray.length - 1 ? <br /> : ""}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </TooltipStyle>
    );
};
// #endregion FUNCTIONS
